import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { COMPANY } from "@/data/services";

function formatEUR(n) {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.round(n || 0));
}

// Pre-cargar logo como data URL para incrustarlo en el PDF
async function loadLogoDataUrl() {
  try {
    const res = await fetch(COMPANY.logo);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function exportBudgetPDF(budget) {
  const logoData = await loadLogoDataUrl();
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;

  // ============ ENCABEZADO ============
  // Fondo azul oscuro corporativo
  doc.setFillColor(10, 25, 47);
  doc.rect(0, 0, W, 130, 'F');

  // Banda lateral verde
  doc.setFillColor(5, 150, 105);
  doc.rect(0, 130, W, 4, 'F');

  // Logo
  if (logoData) {
    try {
      doc.addImage(logoData, 'PNG', M, 20, 78, 78);
    } catch (e) { /* ignore */ }
  }

  // Datos empresa
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(COMPANY.name, M + 92, 42);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(173, 200, 230);
  doc.text(COMPANY.tagline, M + 92, 58);
  doc.setFontSize(9);
  doc.setTextColor(135, 165, 200);
  doc.text(COMPANY.area, M + 92, 72);

  // Bloque contacto derecho
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('CONTACTO', W - M, 32, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 220, 240);
  const contactLines = [
    `Tel: ${COMPANY.whatsappFormatted}`,
    COMPANY.email,
    COMPANY.website,
    `@${COMPANY.instagram}`,
  ];
  contactLines.forEach((l, i) => doc.text(l, W - M, 48 + i * 13, { align: 'right' }));

  let y = 160;

  // ============ TÍTULO PROPUESTA ============
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('PRESUPUESTO ORIENTATIVO', M, y);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text(`Nº ${budget.id}  ·  ${new Date(budget.updatedAt || Date.now()).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}`, M, y + 14);

  y += 36;

  // ============ DATOS CLIENTE ============
  doc.setFillColor(246, 250, 247);
  doc.rect(M, y, W - 2 * M, 88, 'F');
  doc.setDrawColor(5, 150, 105);
  doc.setLineWidth(2);
  doc.line(M, y, M, y + 88);
  doc.setLineWidth(0.5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(5, 150, 105);
  doc.text('DATOS DEL CLIENTE', M + 14, y + 18);

  doc.setTextColor(30, 41, 59);
  doc.setFontSize(10);
  const fields = [
    ['Cliente', budget.clientName || '—', 0, 0],
    ['Teléfono', budget.clientPhone || '—', 0, 1],
    ['Dirección de la obra', budget.clientAddress || '—', 1, 0],
    ['Tipo de obra', budget.workType || '—', 1, 1],
    ['Superficie', `${budget.surface} m²`, 2, 0],
    ['Fecha', new Date(budget.updatedAt || Date.now()).toLocaleDateString('es-ES'), 2, 1],
  ];
  fields.forEach(([k, v, row, col]) => {
    const xc = M + 14 + col * ((W - 2 * M - 28) / 2);
    const yc = y + 36 + row * 16;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(8);
    doc.text(k.toUpperCase(), xc, yc);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(10);
    doc.text(String(v), xc, yc + 11);
  });

  y += 110;

  // ============ TABLA DE PARTIDAS ============
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('Desglose de partidas', M, y);
  y += 8;

  const rows = budget.partidas
    .filter(p => p.incluir)
    .map(p => {
      const total = p.precioFinal * budget.surface;
      const modoLabel = p.modo === 'optimizado' ? 'Optimizado' : p.modo === 'personalizado' ? 'Personalizado' : 'Estándar';
      return [
        { content: p.name, styles: { fontStyle: 'bold' } },
        p.descripcion || '',
        `${budget.surface} m²`,
        `${formatEUR(p.precioFinal)}/m²`,
        modoLabel,
        { content: formatEUR(total), styles: { fontStyle: 'bold', halign: 'right' } },
      ];
    });

  autoTable(doc, {
    startY: y + 6,
    head: [['Partida', 'Descripción', 'Superficie', 'Precio/m²', 'Modo', 'Importe']],
    body: rows,
    headStyles: { fillColor: [5, 150, 105], textColor: 255, fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: [30, 41, 59], cellPadding: 6 },
    alternateRowStyles: { fillColor: [243, 250, 247] },
    columnStyles: {
      0: { cellWidth: 110 },
      1: { cellWidth: 'auto' },
      2: { halign: 'right', cellWidth: 50 },
      3: { halign: 'right', cellWidth: 60 },
      4: { halign: 'center', cellWidth: 55 },
      5: { halign: 'right', cellWidth: 60, fontStyle: 'bold' },
    },
    margin: { left: M, right: M },
    theme: 'plain',
    didDrawPage: () => {
      // Footer en cada página
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`${COMPANY.name}  ·  ${COMPANY.website}  ·  ${COMPANY.email}  ·  ${COMPANY.whatsappFormatted}`, W / 2, H - 24, { align: 'center' });
      doc.text(`Documento generado el ${new Date().toLocaleDateString('es-ES')}  ·  Estimación orientativa`, W / 2, H - 14, { align: 'center' });
    },
  });

  let afterY = doc.lastAutoTable.finalY + 18;

  // ============ TOTALES ============
  const subtotal = budget.partidas.filter(p => p.incluir).reduce((s, p) => s + p.precioFinal * budget.surface, 0);
  const iva = subtotal * 0.10;
  const totalConIva = subtotal + iva;
  const precioM2 = budget.surface > 0 ? subtotal / budget.surface : 0;

  // Nueva página si no caben totales + condiciones
  if (afterY > H - 280) {
    doc.addPage();
    afterY = 60;
  }

  // Caja de totales lado derecho
  const totalsX = W - M - 220;
  const totalsW = 220;

  doc.setFillColor(246, 250, 247);
  doc.rect(totalsX, afterY, totalsW, 100, 'F');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  doc.text('Subtotal (sin IVA):', totalsX + 12, afterY + 22);
  doc.text('IVA reducido 10%:', totalsX + 12, afterY + 40);
  doc.setFontSize(9);
  doc.text('(reforma vivienda habitual)', totalsX + 12, afterY + 52);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text(formatEUR(subtotal), totalsX + totalsW - 12, afterY + 22, { align: 'right' });
  doc.text(formatEUR(iva), totalsX + totalsW - 12, afterY + 40, { align: 'right' });

  // Total destacado
  doc.setFillColor(5, 150, 105);
  doc.rect(totalsX, afterY + 64, totalsW, 36, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('TOTAL CON IVA', totalsX + 12, afterY + 80);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(formatEUR(totalConIva), totalsX + totalsW - 12, afterY + 86, { align: 'right' });

  // Info lateral izquierda
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('Resumen del proyecto', M, afterY + 14);
  doc.setFontSize(8);
  doc.text(`Superficie total: ${budget.surface} m²`, M, afterY + 28);
  doc.text(`Precio medio /m² (sin IVA): ${formatEUR(precioM2)}`, M, afterY + 40);
  doc.text(`Partidas incluidas: ${budget.partidas.filter(p => p.incluir).length}`, M, afterY + 52);

  afterY += 120;

  // ============ CONDICIONES Y GARANTÍA ============
  if (afterY > H - 220) {
    doc.addPage();
    afterY = 60;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('Condiciones del presupuesto', M, afterY);
  afterY += 14;

  const conditions = [
    ['Validez', 'Presupuesto válido durante 30 días desde la fecha de emisión.'],
    ['Forma de pago', '30% al inicio de la obra, 40% durante la ejecución, 30% al finalizar.'],
    ['Plazo de ejecución', 'A confirmar tras visita técnica y firma del contrato.'],
    ['Garantía', '2 años sobre la mano de obra ejecutada por nuestro equipo.'],
    ['Materiales', 'Calidad media-estándar incluida salvo indicación expresa de gama superior.'],
    ['No incluye', 'Licencias, tasas municipales, contenedor, retirada especial de escombros, trabajos estructurales ni elementos premium salvo indicación.'],
  ];
  doc.setFontSize(9);
  conditions.forEach(([k, v]) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 150, 105);
    doc.text(`${k}:`, M, afterY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85);
    const lines = doc.splitTextToSize(v, W - 2 * M - 100);
    doc.text(lines, M + 95, afterY);
    afterY += Math.max(14, lines.length * 11 + 3);
  });

  afterY += 6;

  // Notas privadas
  if (budget.notes) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text('Observaciones', M, afterY);
    afterY += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    const nlines = doc.splitTextToSize(budget.notes, W - 2 * M);
    doc.text(nlines, M, afterY);
    afterY += nlines.length * 11 + 8;
  }

  // ============ DISCLAIMER + FIRMA ============
  if (afterY > H - 130) {
    doc.addPage();
    afterY = 60;
  }

  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  const disclaimer = `Estimación orientativa. El presupuesto definitivo se confirmará tras visita técnica, medición real y revisión del estado de la obra. ${COMPANY.name} es una empresa legalmente registrada con equipo propio, personal asegurado y garantía. IVA aplicado del 10% válido para obras de renovación o reparación en viviendas particulares según normativa española (Art. 91.Uno.2.10º LIVA).`;
  const dlines = doc.splitTextToSize(disclaimer, W - 2 * M);
  doc.text(dlines, M, afterY);
  afterY += dlines.length * 10 + 24;

  // Firma
  doc.setDrawColor(148, 163, 184);
  doc.setLineWidth(0.5);
  doc.line(M, afterY + 30, M + 180, afterY + 30);
  doc.line(W - M - 180, afterY + 30, W - M, afterY + 30);
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text('Por la empresa', M, afterY + 42);
  doc.text(COMPANY.name, M, afterY + 53);
  doc.text('Conforme cliente', W - M, afterY + 42, { align: 'right' });
  doc.text(budget.clientName || '________________', W - M, afterY + 53, { align: 'right' });

  const fileName = `Presupuesto_${(budget.clientName || 'cliente').replace(/\s+/g, '_')}_${budget.id}.pdf`;
  doc.save(fileName);
}
